<template>
  <div>
    <TopBar currentTitle="Meal Plan Collections">
      <b-button type="is-primary" size="is-small" icon-left="plus" @click="createNewMealPlanCollection">Add Collection</b-button>
    </TopBar>
    <div class="page-content">
    <h1 class="page-title">Meal Plan Collections</h1>
    <p class="page-subtitle mb-4">{{ collections.length }} total</p>

    <!-- Filters Bar -->
    <div class="filters-bar">
      <b-select v-model="statusFilter" size="is-small" @input="onStatusFilterChange" placeholder="All statuses">
        <option :value="''">All statuses</option>
        <option value="draft">Draft</option>
        <option value="test">Test</option>
        <option value="fix">Fix</option>
        <option value="live">Live</option>
      </b-select>
    </div>

    <!-- Table Card -->
    <div class="feeds-card">
      <div class="feeds-card-body compact">
        <b-table
          :data="filteredCollections"
          :hoverable="true"
          :mobile-cards="false"
          :default-sort="[sortField, sortDirection]"
          @sort="onSort"
          @click="openMealPlanCollection">

          <b-table-column width=48 label="Image" sortable v-slot="props">
            <figure class="m-0 p-0 image is-48x48">
              <img v-if="rowImageSrc(props.row) == null" class="thumbnail" src="@/assets/listing-placeholder.png">
              <img v-else class="thumbnail" :src="rowImageSrc(props.row)">
            </figure>
          </b-table-column>

          <b-table-column field="title" label="Title" sortable v-slot="props">
            <p class="has-text-weight-bold">{{ props.row.title }}</p>
          </b-table-column>

          <b-table-column field="plans" label="Meal Plans" centered v-slot="props">
            <p class="has-text-weight-bold has-text-centered">{{ rowNumberMealPlans(props.row) }}</p>
          </b-table-column>

          <b-table-column field="position" label="Position" sortable centered v-slot="props">
            <p class="has-text-weight-bold has-text-centered">{{ props.row.position }}</p>
          </b-table-column>

          <b-table-column field="status" width=100 label="Status" sortable centered v-slot="props">
            <FeedsStatusBadge :status="props.row.status" />
          </b-table-column>

          <b-table-column v-if="isDesktop()" field="created_at" width=100 label="Created" sortable centered v-slot="props">
            <span class="date-tag">{{ new Date(props.row.created_at).toLocaleDateString() }}</span>
          </b-table-column>

          <b-table-column v-if="isDesktop()" field="id" width=60 label="ID" v-slot="props">
            <p class="is-size-7 has-text-grey">{{ props.row.id }}</p>
          </b-table-column>

          <b-table-column field="options" custom-key="actions" width=20 v-slot="props">
            <b-dropdown aria-role="list" position="is-bottom-left" @click.native.stop>
              <template #trigger>
                <b-button icon-right="dots-vertical" />
              </template>
              <b-dropdown-item aria-role="listitem" @click="duplicateMealPlanCollection(props.row)">
                <b-icon size="is-small" icon="content-copy"></b-icon>
                Duplicate
              </b-dropdown-item>
              <b-dropdown-item class="has-text-danger" aria-role="listitem" @click="deleteMealPlanCollection(props.row)">
                <b-icon size="is-small" icon="delete"></b-icon>
                Delete
              </b-dropdown-item>
            </b-dropdown>
          </b-table-column>
        </b-table>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import db from "@/firebase/db"
import FeedsStatusBadge from '@/components/common/FeedsStatusBadge.vue'
import TopBar from '@/components/layout/TopBar.vue'

const SCROLL_STATE_KEY = 'feeds_list_state_MealPlanCollectionListView'
const SORT_STATE_KEY = 'feeds_list_sort_MealPlanCollectionListView'
const FILTER_STATE_KEY = 'feeds_list_filter_MealPlanCollectionListView'

export default {
  name: 'MealPlanCollectionListView',
  components: {
    FeedsStatusBadge,
    TopBar
  },
  data() {
    const savedSort = JSON.parse(sessionStorage.getItem(SORT_STATE_KEY) || 'null')
    return {
      mealPlanCollections: [],
      sortField: savedSort?.field || 'created_at',
      sortDirection: savedSort?.direction || 'desc',
      statusFilter: sessionStorage.getItem(FILTER_STATE_KEY) || '',
      scrollRestored: false
    }
  },
  watch: {
    mealPlanCollections() {
      if (!this.scrollRestored) {
        this.scrollRestored = true
        this.$nextTick(() => {
          const savedState = sessionStorage.getItem(SCROLL_STATE_KEY)
          if (savedState) {
            const state = JSON.parse(savedState)
            if (state.scrollY > 0) {
              setTimeout(() => window.scrollTo(state.scrollX, state.scrollY), 50)
            }
          }
        })
      }
    }
  },
  firestore: {
    mealPlanCollections: db.collection('mealplan_collections').orderBy("created_at", "desc")
  },
  computed: {
    collections(){
      return this.mealPlanCollections.filter(collection => collection.type == "admin-created")
    },
    filteredCollections() {
      if (!this.statusFilter) return this.collections
      return this.collections.filter(c => c.status === this.statusFilter)
    }
  },
  methods: {
    isDesktop() {
      return screen.width > 760
    },
    onSort(field, direction) {
      this.sortField = field
      this.sortDirection = direction
      sessionStorage.setItem(SORT_STATE_KEY, JSON.stringify({ field, direction }))
    },
    onStatusFilterChange(value) {
      sessionStorage.setItem(FILTER_STATE_KEY, value)
    },
    createNewMealPlanCollection() {
      db.collection("mealplan_collections").add({
        created_at: Date.now(),
        title: "New meal plan collection",
        description: "",
        plans: {},
        images: [],
        status: "draft",
        type: "admin-created",
        position: 1,
        analytics_identifier: null,
      })
      .then((docRef) => {
        this.$router.push({ name: 'MealPlanCollectionView', params: { id: docRef.id } })
      })
      .catch((error) => {
        console.error("Error adding document: ", error);
      });
    },
    openMealPlanCollection(mealPlanCollection) {
      this.$router.push({ name: 'MealPlanCollectionView', params: { id: mealPlanCollection.id } })
    },
    deleteMealPlanCollection(mealPlanCollection) {
      this.$buefy.dialog.confirm({
        title: 'Deleting meal plan collection',
        message: `Are you sure you want to delete Meal Plan Collection: <b><i>"${mealPlanCollection.title}"</i></b>? <br /><br />This action cannot be undone.`,
        confirmText: 'Delete Meal Plan Collection',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          db.collection("mealplan_collections").doc(mealPlanCollection.id)
          .delete()
          .then(() => {
            this.$buefy.toast.open({type: 'is-success', message: `Meal Plan collection successfully deleted!`})
          }).catch((error) => {
            this.$buefy.toast.open({type: 'is-danger', message: `Error deleting meal plan collection: ${error}`})
          });
        }
      })
    },
    duplicateMealPlanCollection(mealPlanCollection) {
      db.collection("mealplan_collections").doc(mealPlanCollection.id)
        .get()
        .then((doc) => {
          if (doc.exists && doc.data()) {
            let newData = doc.data();
            newData['created_at'] = Date.now();
            newData['status'] = "draft";
            return db.collection("mealplan_collections").add(newData)
              .then((docRef) => {
                this.$buefy.toast.open({type: 'is-success', message: `Meal Plan Collection successfully duplicated! ${docRef.id}`})
              });
          }
        })
        .catch((error) => {
          this.$buefy.toast.open({type: 'is-danger', message: `Error duplicating meal plan collection ${mealPlanCollection.id}: ${error}`})
        });
    },
    rowNumberMealPlans(mealPlanCollection){
      return Object.keys(mealPlanCollection.plans).length
    },
    rowImageSrc(mealPlanCollection) {
      if (mealPlanCollection.images == null) return null;
      return mealPlanCollection.images.length != 0 ? mealPlanCollection.images[0].src : null;
    }
  }
}
</script>

<style scoped>
.thumbnail {
  height: 100% !important;
  object-fit: cover;
  border-radius: var(--radius-sm);
}

.date-tag {
  font-size: 12px;
  color: var(--text-secondary);
}
</style>
